import { Canvas } from 'fabric'
import { enableAligningGuidelines, enableCenteringGuidelines } from 'lib/fabric'
import { useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import axiosClient from 'services/axios-client'
import { useCoverQuery } from 'store/queries'
import { HEIGHT_IN_PX_WITH_BLEED, WIDTH_WITH_BLEED_PX } from '../constants'
import {
  initializeFrontOrBackCover,
  initializeSpineBackground,
  loadBackgroundImage,
  resetCanvasHistory,
} from '../util/helpers'

// We store the images in s3, so we replace the image links with newly signed URLs each time.
// TODO: Figure out cleaner way to do this.
async function resignImageLinks(bookId, covers) {
  const reqs = []

  covers.forEach((cover) =>
    cover.objects.forEach((obj) => {
      if (obj.src) {
        const old = new URL(obj.src)
        const fileName = old.pathname.split('/').pop()

        reqs.push(
          axiosClient
            .get(`book/${bookId}/cover/image/${fileName}`)
            .then((res) => {
              if (res.ok) {
                obj.src = res.data
              }
            }),
        )
      }
    }),
  )

  await Promise.all(reqs)
}

// Get the correct entry from canvas history.
function getCanvasStateFromHistory(coverHistory) {
  return coverHistory.stack[coverHistory.index]
}

async function initializeFromLocalStorage(
  dispatchHistory,
  lsCover,
  frontCanvas,
  spineCanvas,
  backCanvas,
  bookId,
) {
  const frontCover = getCanvasStateFromHistory(lsCover.canvases.front)
  const spineCover = getCanvasStateFromHistory(lsCover.canvases.spine)
  const backCover = getCanvasStateFromHistory(lsCover.canvases.back)

  const covers = [frontCover, spineCover, backCover]

  await resignImageLinks(bookId, covers)

  dispatchHistory({
    type: 'RESET',
    initialState: lsCover,
  })

  return Promise.all([
    frontCanvas.loadFromJSON(frontCover),
    spineCanvas.loadFromJSON(spineCover),
    backCanvas.loadFromJSON(backCover),
  ])
}

export default function useFabricCanvases(
  frontRef,
  spineRef,
  backRef,
  spineWidth,
  pushHistory,
  dispatchHistory,
  bookId,
  coverId,
  templateId,
  pageCount,
  setStrokeWidth,
  setStrokeColorValue,
  setFillColorValue,
  setCoverName,
  setActiveCanvas,
) {
  const routerHistory = useHistory()
  const location = useLocation()

  const [frontCanvas, setFrontCanvas] = useState(null)
  const [frontBackgroundImage, setFrontBackgroundImage] = useState(null)

  const [spineCanvas, setSpineCanvas] = useState(null)
  const [spineBackgroundImage, setSpineBackgroundImage] = useState(null)

  const [backCanvas, setBackCanvas] = useState(null)
  const [backBackgroundImage, setBackBackgroundImage] = useState(null)

  const [activeSelection, setActiveSelection] = useState(null)

  const { data: coverData, isFetching: isCoverDataFetching } =
    useCoverQuery(coverId)
  const { data: templateData, isFetching: isTemplateDataFetching } =
    useCoverQuery(templateId)

  useEffect(
    function runSetup() {
      const isLoadingExistingCover = coverId && !coverData
      const isLoadingTemplate = templateId && !templateData
      if (
        !frontRef.current ||
        frontCanvas ||
        !pageCount ||
        isLoadingExistingCover ||
        isCoverDataFetching ||
        isLoadingTemplate ||
        isTemplateDataFetching
      ) {
        return
      }

      if (coverData?.is_retired) {
        routerHistory.push(`/dashboard/book/${bookId}/publish/cover-templates`)
      }

      // Initialize canvas event handlers
      // TODO: Make an adapter component so we can handle these in the normal react way.
      const setupCanvasEventHandlers = (canvas, canvasName) => {
        canvas.on('object:added', (e) => {
          e.target.canvas.setActiveObject(e.target)
          setActiveSelection([e.target])
          setStrokeWidth(e.target.strokeWidth)
          setStrokeColorValue(e.target.stroke)
          setFillColorValue(e.target.fill)
          pushHistory(canvas, canvasName)
          canvas.requestRenderAll()
        })

        canvas.on('object:modified', () => pushHistory(canvas, canvasName))
        canvas.on('object:removed', () => pushHistory(canvas, canvasName))

        const handleSelection = ({ selected }) => {
          setActiveSelection(selected || [])

          const singleObjectSelected = selected?.length === 1
          if (singleObjectSelected) {
            const obj = selected[0]

            setStrokeWidth(obj.strokeWidth)
            setStrokeColorValue(obj.stroke)
            setFillColorValue(obj.fill)
          }
        }

        canvas.on('selection:created', handleSelection)
        canvas.on('selection:updated', handleSelection)
        canvas.on('selection:cleared', handleSelection)

        enableCenteringGuidelines(canvas)
        enableAligningGuidelines(canvas)
      }

      const newFrontCanvas = new Canvas(frontRef.current, {
        width: WIDTH_WITH_BLEED_PX,
        height: HEIGHT_IN_PX_WITH_BLEED,
      })
      setFrontCanvas(newFrontCanvas)
      setActiveCanvas(newFrontCanvas)

      const newSpineCanvas = new Canvas(spineRef.current, {
        width: spineWidth,
        height: HEIGHT_IN_PX_WITH_BLEED,
      })
      setSpineCanvas(newSpineCanvas)

      const newBackCanvas = new Canvas(backRef.current, {
        width: WIDTH_WITH_BLEED_PX,
        height: HEIGHT_IN_PX_WITH_BLEED,
      })
      setBackCanvas(newBackCanvas)

      // TODO: Figure out cover name setting for all cases.
      let shouldReset = false

      async function initializeCanvases() {
        const lsCover = JSON.parse(localStorage.getItem('cover-history'))

        if (templateData) {
          // Load from template
          await Promise.all([
            newFrontCanvas.loadFromJSON(templateData.frontcover),
            newSpineCanvas.loadFromJSON(templateData.spine),
            newBackCanvas.loadFromJSON(templateData.backcover),
          ])

          shouldReset = true

          routerHistory.replace({
            pathname: location.pathname,
            search: '',
          })
        } else if (coverData && lsCover?.key !== coverData.file_name) {
          // Remote copy only, or outdated version in localstorage.
          setCoverName(coverData.template_name)

          const front = JSON.parse(coverData.frontcover)
          const spine = JSON.parse(coverData.spine)
          const back = JSON.parse(coverData.backcover)

          await resignImageLinks(bookId, [front, spine, back])

          await Promise.all([
            newFrontCanvas.loadFromJSON(front),
            newSpineCanvas.loadFromJSON(spine),
            newBackCanvas.loadFromJSON(back),
          ])

          shouldReset = true
        } else if (lsCover) {
          await initializeFromLocalStorage(
            dispatchHistory,
            lsCover,
            newFrontCanvas,
            newSpineCanvas,
            newBackCanvas,
            bookId,
          )
        } else {
          // Fresh cover
          initializeFrontOrBackCover(newFrontCanvas, true)
          await initializeSpineBackground(newSpineCanvas, spineWidth)
          initializeFrontOrBackCover(newBackCanvas, false)

          shouldReset = true
        }

        // We have to render before the rest of the setup,
        // or the spine and back covers render don't render on initial view.
        // TODO: Figure this out
        newFrontCanvas.requestRenderAll()
        newSpineCanvas.requestRenderAll()
        newBackCanvas.requestRenderAll()

        if (shouldReset) {
          resetCanvasHistory(
            dispatchHistory,
            coverData?.file_name,
            newFrontCanvas,
            newSpineCanvas,
            newBackCanvas,
          )
        }

        newFrontCanvas.requestRenderAll()
        newSpineCanvas.requestRenderAll()
        newBackCanvas.requestRenderAll()

        setupCanvasEventHandlers(newFrontCanvas, 'front')
        setupCanvasEventHandlers(newSpineCanvas, 'spine')
        setupCanvasEventHandlers(newBackCanvas, 'back')
      }

      initializeCanvases()
    },
    [
      coverId,
      coverData,
      pageCount,
      pushHistory,
      spineWidth,
      isCoverDataFetching,
      routerHistory,
      bookId,
      templateId,
      templateData,
      isTemplateDataFetching,
      location.pathname,
      frontRef,
      frontCanvas,
      setActiveCanvas,
      spineRef,
      backRef,
      setStrokeWidth,
      setStrokeColorValue,
      setFillColorValue,
      setCoverName,
      dispatchHistory,
    ],
  )

  // Renders the canvases to data URL images.
  const renderCanvases = useCallback(async () => {
    const [clonedFrontCanvas, clonedSpineCanvas, clonedBackCanvas] =
      await Promise.all([
        frontCanvas.clone(),
        spineCanvas.clone(),
        backCanvas.clone(),
      ])

    clonedFrontCanvas.backgroundImage = null
    if (frontBackgroundImage) {
      clonedFrontCanvas.backgroundImage = await loadBackgroundImage(
        clonedFrontCanvas,
        frontBackgroundImage,
      )
    }
    clonedSpineCanvas.backgroundImage = null
    if (spineBackgroundImage) {
      clonedSpineCanvas.backgroundImage = await loadBackgroundImage(
        clonedSpineCanvas,
        spineBackgroundImage,
      )
    }
    clonedBackCanvas.backgroundImage = null
    if (backBackgroundImage) {
      clonedBackCanvas.backgroundImage = await loadBackgroundImage(
        clonedBackCanvas,
        backBackgroundImage,
      )
    }

    return [
      clonedBackCanvas.toDataURL(),
      clonedSpineCanvas.toDataURL(),
      clonedFrontCanvas.toDataURL(),
    ]
  }, [
    backBackgroundImage,
    frontBackgroundImage,
    spineBackgroundImage,
    frontCanvas,
    backCanvas,
    spineCanvas,
  ])

  return {
    frontCanvas,
    spineCanvas,
    backCanvas,
    renderCanvases,
    frontBackgroundImage,
    spineBackgroundImage,
    backBackgroundImage,
    setBackBackgroundImage,
    setSpineBackgroundImage,
    setFrontBackgroundImage,
    activeSelection,
    setActiveSelection,
  }
}
